import React from "react"
import {
  HStack, Text, Box, IconButton, Button, VStack,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody, Portal,
  useDisclosure,
} from "@chakra-ui/react"
import {
  Control, FieldValues, Path, PathValue, useController,
} from "react-hook-form"
import Icon from "components/elements/Icon"
import PropertyRow from "./PropertyRow"
import ColorPicker from "./ColorPicker"
import { MIXED, useIsMixed } from "./MixedValues"

type Props<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label: string
  /** The color when there's none, and what Remove sets back */
  defaultValue?: string
  /** The colors offered as swatches */
  swatches: readonly string[]
  /** A color wheel and a hex field besides the swatches */
  allowCustom?: boolean
}

/** A color in an editor's property panel: a swatch button opening a picker, and Remove. */
const ColorProperty = <T extends FieldValues, >({
  control,
  name,
  label,
  defaultValue = "transparent",
  swatches,
  allowCustom = true,
}: Props<T>) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { field } = useController({
    name,
    control,
    defaultValue: defaultValue as PathValue<T, Path<T>>,
  })

  const mixed = useIsMixed(name)
  const fillColor = (field.value as string | undefined) || defaultValue
  const hasValidFillColor = fillColor !== "transparent" && !mixed

  const getDisplayText = () => {
    if (mixed) return MIXED
    if (!hasValidFillColor) return "Add..."

    return fillColor
  }

  const clearFill = () => {
    field.onChange(defaultValue)
    onClose()
  }

  return (
    <PropertyRow label={label}>
      <HStack spacing={2}>
        <Popover
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          placement="right-start"
          closeOnBlur
          closeOnEsc
        >
          <PopoverTrigger>
            <Button
              variant="outline"
              size="sm"
              flex={1}
              justifyContent="flex-start"
              bg="white"
              _hover={{ bg: "gray.50" }}
              _focus={{ bg: "gray.50", boxShadow: "none" }}
              borderColor="gray.300"
              px={2}
            >
              <HStack spacing={2}>
                {hasValidFillColor && (
                <Box
                  w={3}
                  h={3}
                  borderRadius="sm"
                  bg={fillColor}
                  borderWidth={1}
                  borderColor="gray.400"
                  flexShrink={0}
                />
                )}
                <Text fontSize="sm" color={hasValidFillColor || mixed ? "black" : "gray.500"}>
                  {getDisplayText()}
                </Text>
              </HStack>
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="320px" zIndex={99999}>
              <PopoverArrow />
              <PopoverBody>
                <VStack spacing={4} p={2}>
                  <Box w="full">
                    <ColorPicker
                      name={name}
                      control={control}
                      defaultValue={defaultValue}
                      swatches={swatches}
                      allowCustom={allowCustom}
                    />
                  </Box>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
        {(hasValidFillColor || mixed) && (
          <IconButton
            aria-label={`Remove ${label.toLowerCase()}`}
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={clearFill}
            fontSize="md"
            icon={<Icon icon="cross" />}
          />
        )}
      </HStack>
    </PropertyRow>
  )
}

export default ColorProperty
